import { useMemo } from 'react'
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material'
import { CanonicalPeriod, EmissionTier, MetricState, metricStateLabels, PERIODS } from '../../common'
import { usePropertyClimateData, type PropertyResult } from '../../../../hooks/climateData'
import { parseWaterStressScore, WaterRiskScoreBadge } from '../../common/waterRisk/utils'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { parseMetricString } from '../../common/financialMetrics'

type Props = {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}

type PeriodSnapshot = {
  score: number | null
  demand: number | null
  demandYear: number | null
  demandInfo: string | null
  supply: number | null
  supplyYear: number | null
  supplyInfo: string | null
  depletion: number | null
  depletionInfo: string | null
  seasonalVariability: number | null
  seasonalVariabilityInfo: string | null
  interannualVariability: number | null
  interannualVariabilityInfo: string | null
}

const emptySnapshot: PeriodSnapshot = {
  score: null,
  demand: null,
  demandYear: null,
  demandInfo: null,
  supply: null,
  supplyYear: null,
  supplyInfo: null,
  depletion: null,
  depletionInfo: null,
  seasonalVariability: null,
  seasonalVariabilityInfo: null,
  interannualVariability: null,
  interannualVariabilityInfo: null,
}

function parsePoint(
  result: PropertyResult | undefined,
  tierKey: string,
  period: CanonicalPeriod,
): { value: number | null; year: number | null } {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) {
    return { value: null, year: null }
  }
  const node = result.data?.data?.[tierKey]?.[period]
  const rawValue =
    typeof node?.y === 'number'
      ? node.y
      : typeof node?.y === 'string'
      ? Number.parseFloat(node.y)
      : typeof node === 'number'
      ? node
      : typeof node === 'string'
      ? Number.parseFloat(node)
      : null
  const value = typeof rawValue === 'number' && !Number.isNaN(rawValue) ? rawValue : null
  const year = typeof node?.x === 'number' ? node.x : null
  return { value, year }
}

function parseInfo(result: PropertyResult | undefined, tierKey: string, period: CanonicalPeriod): string | null {
  if (!result || result.state?.value !== MetricState.SUCCEEDED) return null
  return parseMetricString(
    result.data?.data?.[tierKey]?.[period] ?? result.data?.data?.[tierKey]?.description?.[period] ?? null,
  )
}

const WaterRiskOverview: React.FC<Props> = ({ propertyId, emissionTier, selectedPeriod }) => {
  const tierKey = emissionTier

  const {
    data: waterStress,
    isLoading: waterStressLoading,
    error: waterStressError,
  } = usePropertyClimateData('wr:ws', { propertyId })
  const {
    data: waterDemand,
    isLoading: waterDemandLoading,
    error: waterDemandError,
  } = usePropertyClimateData('wr:ww', { propertyId })
  const {
    data: blueWater,
    isLoading: blueWaterLoading,
    error: blueWaterError,
  } = usePropertyClimateData('wr:ba', { propertyId })
  const { data: waterDemandInfo } = usePropertyClimateData('wr:ww_info', { propertyId })
  const { data: blueWaterInfo } = usePropertyClimateData('wr:ba_info', { propertyId })
  const { data: depletion } = usePropertyClimateData('wr:wd', { propertyId })
  const { data: depletionInfo } = usePropertyClimateData('wr:wd_info', { propertyId })
  const { data: seasonalVariability } = usePropertyClimateData('wr:sv', { propertyId })
  const { data: seasonalVariabilityInfo } = usePropertyClimateData('wr:sv_info', { propertyId })
  const { data: interannualVariability } = usePropertyClimateData('wr:iv', { propertyId })
  const { data: interannualVariabilityInfo } = usePropertyClimateData('wr:iv_info', { propertyId })

  const snapshots = useMemo(() => {
    const out: Record<CanonicalPeriod, PeriodSnapshot> = {
      [CanonicalPeriod.SHORT_TERM]: { ...emptySnapshot },
      [CanonicalPeriod.MID_TERM]: { ...emptySnapshot },
      [CanonicalPeriod.LONG_TERM]: { ...emptySnapshot },
    }

    PERIODS.forEach(period => {
      const pointDemand = parsePoint(waterDemand, tierKey, period)
      const pointSupply = parsePoint(blueWater, tierKey, period)
      out[period] = {
        score: parseWaterStressScore(waterStress, tierKey, period),
        demand: pointDemand.value,
        demandYear: pointDemand.year,
        demandInfo: parseInfo(waterDemandInfo, tierKey, period),
        supply: pointSupply.value,
        supplyYear: pointSupply.year,
        supplyInfo: parseInfo(blueWaterInfo, tierKey, period),
        depletion: parsePoint(depletion, tierKey, period).value,
        depletionInfo: parseInfo(depletionInfo, tierKey, period),
        seasonalVariability: parsePoint(seasonalVariability, tierKey, period).value,
        seasonalVariabilityInfo: parseInfo(seasonalVariabilityInfo, tierKey, period),
        interannualVariability: parsePoint(interannualVariability, tierKey, period).value,
        interannualVariabilityInfo: parseInfo(interannualVariabilityInfo, tierKey, period),
      }
    })

    return out
  }, [
    blueWater,
    blueWaterInfo,
    depletion,
    depletionInfo,
    interannualVariability,
    interannualVariabilityInfo,
    seasonalVariability,
    seasonalVariabilityInfo,
    tierKey,
    waterDemand,
    waterDemandInfo,
    waterStress,
  ])

  const periodsWithData = useMemo(
    () =>
      PERIODS.filter(period => {
        const snapshot = snapshots[period]
        return snapshot.score != null || snapshot.demand != null || snapshot.supply != null
      }),
    [snapshots],
  )

  const current = snapshots[selectedPeriod]

  const loading = waterStressLoading || waterDemandLoading || blueWaterLoading
  const hasData = periodsWithData.length > 0
  const allErrored = !!(waterStressError && waterDemandError && blueWaterError)

  if (loading && !hasData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Unable to load water risk metrics for this property.</Alert>
  }

  if (!hasData) {
    return <Alert severity="info">No water risk data is available for this property.</Alert>
  }

  const balance = current.demand != null && current.supply != null ? current.supply - current.demand : null
  const hasDriverData =
    current.depletion != null || current.seasonalVariability != null || current.interannualVariability != null
  const riskRatingHelpText = 'Water stress risk rating for the selected horizon and emissions scenario.'
  const demandHelpText =
    'Total annual water demanded by households, farms and industry in the local basin, shown as water depth (cm/year).'
  const supplyHelpText =
    'Average annual renewable freshwater available in the local basin, shown as water depth (cm/year).'
  const depletionHelpText = 'Consumptive water use compared with renewable supply.'
  const seasonalVariabilityHelpText = 'How uneven available water is across wet and dry months.'
  const interannualVariabilityHelpText = 'How much available water can vary from year to year.'

  return (
    <Box>
      <Box
        mb={3}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <ClimateSectionTitle
          title="Overview"
          subtitle="Summary of water risk for the selected period"
          helpText="Overview metrics use the horizon selected in the page header."
          loading={loading}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="Risk Rating" helpText={riskRatingHelpText} loading={waterStressLoading}>
            {current.score != null ? (
              <Box>
                <WaterRiskScoreBadge score={current.score} size="regular" />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {waterStress?.state?.value && waterStress.state.value !== MetricState.SUCCEEDED
                  ? `Water stress data is ${metricStateLabels[waterStress.state.value]}.`
                  : 'Water stress data is unavailable for this period.'}
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard
            title="Gross Water Demand"
            subtitle={current.demandYear ? String(current.demandYear) : undefined}
            helpText={demandHelpText}
            loading={waterDemandLoading}
          >
            {current.demand != null ? (
              <>
                <Typography variant="h4" component="div">
                  {current.demand.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {current.demandInfo ?? demandHelpText}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {waterDemand?.state?.value && waterDemand.state.value !== MetricState.SUCCEEDED
                  ? `Water demand data is ${metricStateLabels[waterDemand.state.value]}.`
                  : 'Water demand data is unavailable for this period.'}
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard
            title="Available Blue Water"
            subtitle={current.supplyYear ? String(current.supplyYear) : undefined}
            helpText={supplyHelpText}
            loading={blueWaterLoading}
          >
            {current.supply != null ? (
              <>
                <Typography variant="h4" component="div">
                  {current.supply.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {current.supplyInfo ?? supplyHelpText}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {blueWater?.state?.value && blueWater.state.value !== MetricState.SUCCEEDED
                  ? `Blue water data is ${metricStateLabels[blueWater.state.value]}.`
                  : 'Blue water data is unavailable for this period.'}
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>

      {balance != null && (
        <Box mt={3}>
          <ClimateSectionTitle
            title="Balance"
            helpText="Difference between available blue water and gross water demand for the selected horizon."
          />
          <Typography variant="body2" color="text.secondary">
            {balance >= 0
              ? `Projected supply exceeds demand by ${balance.toFixed(2)} cm/year.`
              : `Projected demand exceeds supply by ${(balance * -1).toFixed(2)} cm/year.`}
          </Typography>
        </Box>
      )}

      {hasDriverData && (
        <Box mt={4}>
          <ClimateSectionTitle
            title="Water Stress Drivers"
            subtitle="Lower-detail Aqueduct drivers behind the overall water stress score"
            helpText="Supporting Aqueduct driver metrics for the selected horizon."
          />

          <Grid container spacing={3} mt={0.5}>
            <Grid item xs={12} md={4}>
              <ClimateMetricCard title="Baseline Water Depletion" helpText={depletionHelpText}>
                {current.depletion != null ? (
                  <>
                    <Typography variant="h4">
                      {current.depletion.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {current.depletionInfo ?? depletionHelpText}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Baseline water depletion data is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title="Seasonal Variability" helpText={seasonalVariabilityHelpText}>
                {current.seasonalVariability != null ? (
                  <>
                    <Typography variant="h4">
                      {current.seasonalVariability.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {current.seasonalVariabilityInfo ?? seasonalVariabilityHelpText}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Seasonal variability data is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title="Interannual Variability" helpText={interannualVariabilityHelpText}>
                {current.interannualVariability != null ? (
                  <>
                    <Typography variant="h4">
                      {current.interannualVariability.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {current.interannualVariabilityInfo ?? interannualVariabilityHelpText}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Interannual variability data is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  )
}

export default WaterRiskOverview
